"use client";

/**
 * OrnateFrame — geometric museum-plaque frame with draw-in animation.
 *
 * Scene 0 (OpeningHookScene) investiture visual.
 *
 * Visual language: thin stroked rectangle + 4 corner L-bracket ornaments
 * with a single inner tick + dot. NOT Victorian florid — geometric, modern,
 * reads as a formal nameplate rather than a decree scroll.
 *
 * Animation timeline (from `delay` t=0):
 *   0ms   top edge scaleX 0 → 1, transform-origin left   (550ms)
 *   150ms right + left edges scaleY 0 → 1                (500ms)
 *   300ms bottom edge scaleX 0 → 1, transform-origin right (550ms)
 *   600ms 4 corner ornaments fade-in + scale-up           (400ms)
 *
 * Reduced-motion: all lines render instantly, no animation.
 */

import { motion, useReducedMotion } from "framer-motion";
import { cn } from "@/lib/utils";

// Cubic-bezier tuple — declared `as const` so framer-motion's Easing type accepts it.
const EASE_OUT_EXPO: [number, number, number, number] = [0.22, 1, 0.36, 1];

interface OrnateFrameProps {
  /** CSS color (hex, hsl, rgb) used for lines + corner ornaments. */
  accentHex: string;
  /** Seconds to wait before starting draw-in. Default 0. */
  delay?: number;
  /** Extra wrapper classes (usually width / max-width). */
  className?: string;
  /** Text area — rendered inside the frame with standard padding. */
  children: React.ReactNode;
}

type Corner = "tl" | "tr" | "br" | "bl";

const CORNER_POSITION: Record<Corner, string> = {
  tl: "top-0 left-0",
  tr: "top-0 right-0 rotate-90",
  br: "bottom-0 right-0 rotate-180",
  bl: "bottom-0 left-0 -rotate-90",
};

function CornerOrnament({
  placement,
  accentHex,
  delay,
}: {
  placement: Corner;
  accentHex: string;
  delay: number;
}) {
  const reduced = useReducedMotion();
  return (
    <motion.div
      aria-hidden
      className={cn("pointer-events-none absolute", CORNER_POSITION[placement])}
      initial={reduced ? false : { opacity: 0, scale: 0.65 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={
        reduced
          ? { duration: 0 }
          : { duration: 0.4, delay, ease: EASE_OUT_EXPO }
      }
    >
      <svg
        width="52"
        height="52"
        viewBox="0 0 52 52"
        fill="none"
        aria-hidden
      >
        {/* Outer L-bracket — thicker */}
        <path
          d="M 0 22 L 0 0 L 22 0"
          stroke={accentHex}
          strokeWidth="1.5"
          strokeOpacity="0.85"
          fill="none"
          strokeLinecap="round"
        />
        {/* Inner tick — thin, shorter */}
        <path
          d="M 9 9 L 15 9 M 9 9 L 9 15"
          stroke={accentHex}
          strokeWidth="0.9"
          strokeOpacity="0.55"
          fill="none"
          strokeLinecap="round"
        />
        {/* Center dot at the inner corner */}
        <circle cx="9" cy="9" r="1.3" fill={accentHex} fillOpacity="0.85" />
      </svg>
    </motion.div>
  );
}

export function OrnateFrame({
  accentHex,
  delay = 0,
  className,
  children,
}: OrnateFrameProps) {
  const reduced = useReducedMotion();

  const edgeLength = 0.55; // seconds — top/bottom strokes
  const sideLength = 0.5; // seconds — left/right strokes
  const cornerDelay = delay + 0.6;

  const topTransition = reduced
    ? { duration: 0 }
    : { duration: edgeLength, delay, ease: EASE_OUT_EXPO };
  const sidesTransition = reduced
    ? { duration: 0 }
    : { duration: sideLength, delay: delay + 0.15, ease: EASE_OUT_EXPO };
  const bottomTransition = reduced
    ? { duration: 0 }
    : { duration: edgeLength, delay: delay + 0.3, ease: EASE_OUT_EXPO };

  return (
    <div className={cn("relative", className)}>
      {/* Top edge — draws from left to right */}
      <motion.div
        aria-hidden
        className="pointer-events-none absolute inset-x-10 top-0 h-px"
        style={{
          background: accentHex,
          opacity: 0.75,
          transformOrigin: "left center",
        }}
        initial={reduced ? false : { scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={topTransition}
      />

      {/* Right edge — draws from top to bottom */}
      <motion.div
        aria-hidden
        className="pointer-events-none absolute inset-y-10 right-0 w-px"
        style={{
          background: accentHex,
          opacity: 0.75,
          transformOrigin: "center top",
        }}
        initial={reduced ? false : { scaleY: 0 }}
        animate={{ scaleY: 1 }}
        transition={sidesTransition}
      />

      {/* Bottom edge — draws from right to left */}
      <motion.div
        aria-hidden
        className="pointer-events-none absolute inset-x-10 bottom-0 h-px"
        style={{
          background: accentHex,
          opacity: 0.75,
          transformOrigin: "right center",
        }}
        initial={reduced ? false : { scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={bottomTransition}
      />

      {/* Left edge — draws from bottom to top */}
      <motion.div
        aria-hidden
        className="pointer-events-none absolute inset-y-10 left-0 w-px"
        style={{
          background: accentHex,
          opacity: 0.75,
          transformOrigin: "center bottom",
        }}
        initial={reduced ? false : { scaleY: 0 }}
        animate={{ scaleY: 1 }}
        transition={sidesTransition}
      />

      {/* 4 corner ornaments */}
      <CornerOrnament placement="tl" accentHex={accentHex} delay={cornerDelay} />
      <CornerOrnament placement="tr" accentHex={accentHex} delay={cornerDelay + 0.05} />
      <CornerOrnament placement="br" accentHex={accentHex} delay={cornerDelay + 0.1} />
      <CornerOrnament placement="bl" accentHex={accentHex} delay={cornerDelay + 0.15} />

      {/* Top notch — small diamond that interrupts the top edge center.
       * Fill matches scene bg (#0b0f1e) so it reads as a cut-out in the line.
       * Adds plaque-like character; breaks the monotone rectangle. */}
      <EdgeNotch
        accentHex={accentHex}
        delay={cornerDelay + 0.2}
        placement="top"
      />
      <EdgeNotch
        accentHex={accentHex}
        delay={cornerDelay + 0.25}
        placement="bottom"
      />

      {/* Inner content */}
      <div className="relative z-[1] px-10 py-12 lg:px-16 lg:py-16">
        {children}
      </div>
    </div>
  );
}

function EdgeNotch({
  accentHex,
  delay,
  placement,
}: {
  accentHex: string;
  delay: number;
  placement: "top" | "bottom";
}) {
  const reduced = useReducedMotion();
  const positionClass =
    placement === "top"
      ? "top-0 -translate-y-1/2"
      : "bottom-0 translate-y-1/2";
  return (
    <motion.div
      aria-hidden
      className={cn(
        "pointer-events-none absolute left-1/2 -translate-x-1/2",
        positionClass,
      )}
      initial={reduced ? false : { opacity: 0, scale: 0.6 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={
        reduced
          ? { duration: 0 }
          : { duration: 0.35, delay, ease: EASE_OUT_EXPO }
      }
    >
      <svg width="18" height="10" viewBox="0 0 18 10" fill="none" aria-hidden>
        <path
          d="M 9 0 L 18 5 L 9 10 L 0 5 Z"
          fill="#0b0f1e"
          stroke={accentHex}
          strokeWidth="1"
          strokeOpacity="0.8"
        />
        <circle cx="9" cy="5" r="1.2" fill={accentHex} fillOpacity="0.9" />
      </svg>
    </motion.div>
  );
}
